import React, { useState, useEffect } from 'react'
import FyMonthlyBarChart from '../../components/Charts/FyMonthlyBarChart'
import { currentFyLabel } from '../../utils/financialYearMonths'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import assetsDashboardService from '../../services/assetsDashboardService'
import { formatCurrency } from '../../utils/currencyFormat'
import { 
  Users,
  Activity,
  Clock,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Brain,
  DollarSign,
  Target,
  AlertTriangle,
  CheckCircle,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  MessageSquare,
  FileText,
  Briefcase,
  Package,
  Monitor,
  Smartphone,
  Wrench,
  MapPin
} from 'lucide-react'
import { useModuleView } from '../../hooks/useModuleView'
import ModuleOverviewCard from '../../components/Module/ModuleOverviewCard'
import ModuleViewBackLink from '../../components/Module/ModuleViewBackLink'

const Assets = () => {
  const navigate = useNavigate()
  const moduleView = useModuleView()
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const response = await assetsDashboardService.getDashboardStats()
      if (response.success) {
        setDashboardData(response.data)
      } else if (!response._errorHandled) {
        toast.error(response.message || 'Failed to load dashboard data')
      }
    } catch (error) {
      console.error('Error loading assets dashboard data:', error)
      if (!error._errorHandled) {
        if (error.response) {
          toast.error(error.response.data?.message || 'Failed to load dashboard data')
        } else if (!error.request) {
          toast.error('Failed to load dashboard data')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const formatCurrencyDisplay = (amount, currency = 'INR') => formatCurrency(amount, currency, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Just now'
    const date = new Date(timestamp)
    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)
    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`
    return date.toLocaleDateString()
  }

  return (
    <div className="sales-dashboard">
      {/* Page Header */}
      <div className="page-header" style={{textAlign: 'left'}}>
        <div className="page-title" style={{textAlign: 'left', alignItems: 'flex-start'}}>
          <h1 style={{textAlign: 'left', margin: '0 0 4px 0'}}>Assets</h1>
          <p style={{textAlign: 'left', margin: 0}}>Track and manage company assets and equipment</p>
        </div>
      </div>

      {/* Tab Content */}
      {moduleView === 'overview' && (
        <div className="tab-content">
          <ModuleViewBackLink onClick={() => navigate('/assets')} />

          {/* Key Metrics Grid */}
          <div className="dashboard-metrics-grid">
            {/* Asset Metrics */}
            <div className="metric-card revenue">
              <div className="metric-header">
                <div className="metric-icon">
                  <Package size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.totalAssets?.changeType || 'positive'}`}>
                  <TrendingUp size={12} />
                  <span>{dashboardData?.totalAssets?.change >= 0 ? '+' : ''}{dashboardData?.totalAssets?.change ?? 0}</span>
                </div>
              </div>
              <div className="metric-value">{loading ? '...' : (dashboardData?.totalAssets?.count ?? 0)}</div>
              <div className="metric-title">Total Assets</div>
              <div className="metric-subtitle">Active inventory</div>
            </div>

            <div className="metric-card customers">
              <div className="metric-header">
                <div className="metric-icon">
                  <DollarSign size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.totalValue?.changeType || 'positive'}`}>
                  <TrendingUp size={12} />
                  <span>{dashboardData?.totalValue?.change >= 0 ? '+' : ''}{dashboardData?.totalValue?.change?.toFixed(1) ?? 0}%</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : formatCurrencyDisplay(dashboardData?.totalValue?.value ?? 0, dashboardData?.totalValue?.currency || 'INR')}
              </div>
              <div className="metric-title">Asset Value</div>
              <div className="metric-subtitle">Received assets (INR)</div>
            </div>

            <div className="metric-card churn">
              <div className="metric-header">
                <div className="metric-icon">
                  <AlertTriangle size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.maintenanceDue?.count > 0 ? 'negative' : 'positive'}`}>
                  <TrendingDown size={12} />
                  <span>{dashboardData?.maintenanceDue?.status || 'CLEAR'}</span>
                </div>
              </div>
              <div className="metric-value">{loading ? '...' : (dashboardData?.maintenanceDue?.count ?? 0)}</div>
              <div className="metric-title">Maintenance Due</div>
              <div className="metric-subtitle">Next 30 days</div>
            </div>

            <div className="metric-card burn">
              <div className="metric-header">
                <div className="metric-icon">
                  <Users size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.utilizationRate?.status === 'EXCELLENT' ? 'positive' : dashboardData?.utilizationRate?.status === 'NO_DATA' ? 'neutral' : 'negative'}`}>
                  <CheckCircle size={12} />
                  <span>{(dashboardData?.utilizationRate?.status || 'NO DATA').replace(/_/g, ' ')}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading
                  ? '...'
                  : dashboardData?.utilizationRate?.status === 'NO_DATA'
                    ? '—'
                    : `${dashboardData?.utilizationRate?.rate ?? 0}%`}
              </div>
              <div className="metric-title">Assignment Rate</div>
              <div className="metric-subtitle">Received assets assigned</div>
            </div>
          </div>

          {/* Action Items & Alerts */}
          <div className="dashboard-action-section">
            <div className="dashboard-action-items">
              <div className="section-header">
                <h3>Asset Action Items</h3>
                <span className="item-count">{dashboardData?.actionItems?.length ?? 0} items</span>
              </div>
              <div className="action-list">
                {loading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading action items...</div>
                ) : dashboardData?.actionItems?.length > 0 ? (
                  dashboardData.actionItems.map((item, index) => {
                    const iconMap = { maintenance: Wrench, assignment: Users, acquisition: Package }
                    const Icon = iconMap[item.type] || AlertTriangle
                    return (
                      <div key={index} className={`action-item ${item.priority}`}>
                        <div className="action-icon"><Icon size={16} /></div>
                        <div className="action-content">
                          <div className="action-title">{item.title}</div>
                          <div className="action-description">{item.description}</div>
                        </div>
                        <div className="action-time">{formatTimeAgo(item.timestamp)}</div>
                      </div>
                    )
                  })
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>No action items</div>
                )}
              </div>
            </div>

            <div className="dashboard-team-activity">
              <div className="section-header">
                <h3>Asset Team Activity</h3>
                <span className="activity-count">{dashboardData?.teamActivity?.length ?? 0} updates</span>
              </div>
              <div className="activity-list">
                {loading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading team activity...</div>
                ) : dashboardData?.teamActivity?.length > 0 ? (
                  dashboardData.teamActivity.map((activity, index) => (
                    <div key={index} className="activity-item">
                      <div className="activity-avatar">{activity.employeeInitials || 'UN'}</div>
                      <div className="activity-content">
                        <div className="activity-text">
                          <strong>{activity.employeeName || 'Unknown'}</strong> {activity.activity}
                        </div>
                        <div className="activity-time">{formatTimeAgo(activity.timestamp)}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>No recent activity</div>
                )}
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="dashboard-charts-section">
            <div className="dashboard-chart-card revenue-chart">
              <div className="chart-header">
                <h3>Asset Value Trend</h3>
                <p>{dashboardData?.financialYear ? `FY ${dashboardData.financialYear}` : `FY ${currentFyLabel()}`}</p>
              </div>
              <div className="chart-content">
                <FyMonthlyBarChart
                  series={dashboardData?.assetValueTrend}
                  valueKey="revenue"
                  formatTooltip={(v) => formatCurrencyDisplay(v, 'INR')}
                  emptyMessage="No acquisitions this financial year"
                />
              </div>
            </div>

            <div className="dashboard-chart-card customer-health">
              <div className="chart-header">
                <h3>Asset Category Distribution</h3>
                <p>Received assets</p>
              </div>
              <div className="chart-content">
                <div className="health-distribution">
                  {loading ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading...</div>
                  ) : dashboardData?.typeDistribution?.length > 0 ? (
                    dashboardData.typeDistribution.map((row, index) => {
                      const classes = ['excellent', 'good', 'at-risk', 'churned']
                      const cls = classes[index % classes.length]
                      return (
                        <div key={row.name || index} className={`health-segment ${cls}`}>
                          <div className="segment-bar" style={{ width: `${Math.max(row.percent || 0, row.count > 0 ? 4 : 0)}%` }} />
                          <div className="segment-info">
                            <span className="segment-label">{row.name}</span>
                            <span className="segment-count">{row.count}</span>
                          </div>
                        </div>
                      )
                    })
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>No received assets</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'masters' && (
        <div className="tab-content">
          <div className="assets-masters-content">
            <div className="assets-masters-header">
              <h2>Asset Masters</h2>
              <p>Manage your asset master data and configurations</p>
            </div>
            <div className="assets-masters-grid">
              <div 
                className="assets-master-card"
                onClick={() => navigate('/assets/asset-type-master')}
                style={{ cursor: 'pointer' }}
              >
                <div className="assets-master-icon">
                  <Package size={24} />
                </div>
                <div className="assets-master-content">
                  <h3>Asset Types</h3>
                  <p>Asset categories and classifications</p>
                  <div className="assets-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.assetTypes ?? 0} Types`}
                  </div>
                </div>
              </div>
              <div
                className="assets-master-card"
                onClick={() => navigate('/assets/location-master')}
                style={{ cursor: 'pointer' }}
              >
                <div className="assets-master-icon">
                  <MapPin size={24} />
                </div>
                <div className="assets-master-content">
                  <h3>Locations</h3>
                  <p>Asset locations and storage areas</p>
                  <div className="assets-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.locations ?? 0} Locations`}
                  </div>
                </div>
              </div>
              <div
                className="assets-master-card"
                onClick={() => navigate('/assets/vendor-master')}
                style={{ cursor: 'pointer' }}
              >
                <div className="assets-master-icon">
                  <Target size={24} />
                </div>
                <div className="assets-master-content">
                  <h3>Vendors</h3>
                  <p>Asset suppliers and service providers</p>
                  <div className="assets-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.vendors ?? 0} Vendors`}
                  </div>
                </div>
              </div>
              <div
                className="assets-master-card"
                onClick={() => navigate('/assets/maintenance-provider-master')}
                style={{ cursor: 'pointer' }}
              >
                <div className="assets-master-icon">
                  <Wrench size={24} />
                </div>
                <div className="assets-master-content">
                  <h3>Maintenance</h3>
                  <p>Maintenance schedules and service providers</p>
                  <div className="assets-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.maintenanceProviders ?? 0} Providers`}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'transactions' && (
        <div className="tab-content">
          <div className="assets-transactions-content">
            <div className="assets-transactions-header">
              <h2>Asset Transactions</h2>
              <p>Track and manage all asset lifecycle activities</p>
            </div>
            <div className="assets-transactions-grid">
              <ModuleOverviewCard
                prefix="assets-transaction"
                moduleName="Assets"
                showArrow={false}
                onClick={() => navigate('/assets?view=overview')}
              />
              <div
                className="assets-transaction-card"
                onClick={() => navigate('/assets/acquisitions')}
                style={{ cursor: 'pointer' }}
              >
                <div className="assets-transaction-icon">
                  <Package size={24} />
                </div>
                <div className="assets-transaction-content">
                  <h3>Asset Acquisitions</h3>
                  <p>New asset purchases and acquisitions</p>
                  <div className="assets-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.acquisitions ?? 0} Records`}
                  </div>
                </div>
              </div>
              <div
                className="assets-transaction-card"
                onClick={() => navigate('/assets/assignments')}
                style={{ cursor: 'pointer' }}
              >
                <div className="assets-transaction-icon">
                  <Users size={24} />
                </div>
                <div className="assets-transaction-content">
                  <h3>Assignments</h3>
                  <p>Asset assignments and transfers</p>
                  <div className="assets-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.assignments ?? 0} Records`}
                  </div>
                </div>
              </div>
              <div
                className="assets-transaction-card"
                onClick={() => navigate('/assets/maintenance')}
                style={{ cursor: 'pointer' }}
              >
                <div className="assets-transaction-icon">
                  <Wrench size={24} />
                </div>
                <div className="assets-transaction-content">
                  <h3>Maintenance</h3>
                  <p>Asset maintenance and repairs</p>
                  <div className="assets-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.maintenance ?? 0} Records`}
                  </div>
                </div>
              </div>
              <div
                className="assets-transaction-card"
                onClick={() => navigate('/assets/disposals')}
                style={{ cursor: 'pointer' }}
              >
                <div className="assets-transaction-icon">
                  <Activity size={24} />
                </div>
                <div className="assets-transaction-content">
                  <h3>Disposals</h3>
                  <p>Asset disposals and write-offs</p>
                  <div className="assets-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.disposals ?? 0} Records`}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'reports' && (
        <div className="tab-content">
          <div className="assets-reports-content">
            <div className="assets-reports-header">
              <h2>Asset Reports</h2>
              <p>Comprehensive asset analytics and reporting</p>
            </div>
            <div className="assets-reports-grid">
              <div
                className="assets-report-card"
                onClick={() => navigate('/assets/reports/valuation')}
                style={{ cursor: 'pointer' }}
              >
                <div className="assets-report-icon">
                  <BarChart3 size={24} />
                </div>
                <div className="assets-report-content">
                  <h3>Asset Valuation</h3>
                  <p>Asset value and depreciation analysis</p>
                  <div className="assets-report-status">View Report</div>
                </div>
              </div>
              <div
                className="assets-report-card"
                onClick={() => navigate('/assets/reports/utilization')}
                style={{ cursor: 'pointer' }}
              >
                <div className="assets-report-icon">
                  <Users size={24} />
                </div>
                <div className="assets-report-content">
                  <h3>Utilization Reports</h3>
                  <p>Asset utilization and efficiency metrics</p>
                  <div className="assets-report-status">View Report</div>
                </div>
              </div>
              <div
                className="assets-report-card"
                onClick={() => navigate('/assets/reports/maintenance')}
                style={{ cursor: 'pointer' }}
              >
                <div className="assets-report-icon">
                  <Wrench size={24} />
                </div>
                <div className="assets-report-content">
                  <h3>Maintenance Reports</h3>
                  <p>Maintenance schedules and cost analysis</p>
                  <div className="assets-report-status">View Report</div>
                </div>
              </div>
              <div
                className="assets-report-card"
                onClick={() => navigate('/assets/reports/financial')}
                style={{ cursor: 'pointer' }}
              >
                <div className="assets-report-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="assets-report-content">
                  <h3>Financial Reports</h3>
                  <p>Asset financial impact and ROI analysis</p>
                  <div className="assets-report-status">View Report</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Assets
